import type { BoxProps } from '@chakra-ui/react';
import { LinkBox, LinkOverlay, Stack, Text } from '@chakra-ui/react';
import { Mitopure } from '@time-line/components/icons';
import type { BannerCard } from 'hooks/useBannerCard';
import { useImage } from 'lib/sanity';
import Image from 'next/image';
import Link from 'next/link';
import type { ReactNode } from 'react';

export type BannerCardVariant = 'default' | 'wide';

export interface BannerCardContentProps extends BoxProps {
  onCtaClick: (url: string) => void;
  card: BannerCard;
  header?: ReactNode;
  variant?: BannerCardVariant;
}

export function BannerCardContent({
  onCtaClick,
  card,
  header,
  variant = 'default',
  ...props
}: BannerCardContentProps) {
  const { title, cta, backgroundImage, backgroundImageWide } = card;

  const image = variant === 'wide' ? backgroundImageWide : backgroundImage;
  const imageProps = useImage(image, { fill: true });

  return (
    <LinkBox
      as="article"
      pos="relative"
      overflow="hidden"
      sx={{
        containerType: 'inline-size',
      }}
      {...props}
    >
      {imageProps && (
        <Image
          {...imageProps}
          alt=""
          role="presentation"
          style={{ objectFit: 'cover' }}
        />
      )}
      {header}
      <Stack
        pos="static" // Ensures the entire card is clickable with LinkBox
        isolation="isolate"
        gap="2xs"
        textAlign="center"
        alignItems="center"
      >
        <Mitopure
          color="red.300"
          width="89px"
          height="auto"
          sx={{
            [`@container (max-width: 260px)`]: {
              width: '80px',
            },
          }}
        />
        <Text
          as="h2"
          size={{
            base: variant === 'wide' ? 'h4' : 'h3',
            md: 'h4',
          }}
          sx={{
            textWrap: 'pretty',
            ...(variant === 'default' && {
              [`@container (max-width: 260px)`]: {
                fontSize: 'lg',
              },
            }),
          }}
        >
          {title}
        </Text>
        <LinkOverlay
          as={Link}
          href={cta.href}
          onClick={() => onCtaClick(cta.href)}
          fontSize={0}
        >
          <Text
            as="span"
            size="overline"
            sx={{
              textDecoration: 'underline 1px',
              textUnderlineOffset: '0.4em',
            }}
          >
            {cta.title}
          </Text>
        </LinkOverlay>
      </Stack>
    </LinkBox>
  );
}
